import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';

export default class MeritDashboardPage extends Page {
    loading: boolean = true;
    data: any = null;
    receiversTab: string = 'allTime';

    oninit(vnode: any) {
        super.oninit(vnode);
        app.setTitle(app.translator.trans('predictiontalk-merit-system.forum.dashboard.title') as string);
        this.loadData();
    }

    async loadData() {
        this.loading = true;
        m.redraw();

        try {
            this.data = await app.request({
                method: 'GET',
                url: app.forum.attribute('apiUrl') + '/merit/dashboard',
            });
        } catch (e) {
            console.error('Failed to load dashboard:', e);
        }

        this.loading = false;
        m.redraw();
    }

    view() {
        return (
            <div className="MeritDashboardPage">
                <h2>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.title')}</h2>

                {this.loading
                    ? <LoadingIndicator />
                    : this.data
                        ? this.renderDashboard()
                        : <p>Failed to load dashboard data.</p>
                }
            </div>
        );
    }

    renderDashboard() {
        const data = this.data;

        return (
            <div>
                {/* Recent Transactions */}
                <div className="dashboard-section">
                    <h3>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.recent_transactions')}</h3>
                    {data.recentTransactions?.length > 0
                        ? <table>
                            <thead>
                                <tr>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.date')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.sender')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.receiver')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.type')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.amount')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.reason')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recentTransactions.map((tx: any) => (
                                    <tr>
                                        <td>{tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : '-'}</td>
                                        <td>
                                            {tx.sender
                                                ? <Link href={app.route('user', { username: tx.sender.username })}>{tx.sender.displayName}</Link>
                                                : '-'
                                            }
                                        </td>
                                        <td>
                                            {tx.receiver
                                                ? <Link href={app.route('user', { username: tx.receiver.username })}>{tx.receiver.displayName}</Link>
                                                : '-'
                                            }
                                        </td>
                                        <td><span className={tx.type === 'merit' ? 'merit-count' : 'smerit-count'}>{tx.type}</span></td>
                                        <td>{tx.amount}</td>
                                        <td>{tx.reason}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        : <div className="no-data">{app.translator.trans('predictiontalk-merit-system.forum.dashboard.no_transactions')}</div>
                    }
                </div>

                {/* Top Receivers */}
                <div className="dashboard-section">
                    <h3>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.top_receivers')}</h3>
                    <div className="dashboard-tabs">
                        <Button
                            className={'Button' + (this.receiversTab === 'allTime' ? ' active' : '')}
                            onclick={() => { this.receiversTab = 'allTime'; }}
                        >
                            {app.translator.trans('predictiontalk-merit-system.forum.dashboard.top_receivers_all')}
                        </Button>
                        <Button
                            className={'Button' + (this.receiversTab === 'monthly' ? ' active' : '')}
                            onclick={() => { this.receiversTab = 'monthly'; }}
                        >
                            {app.translator.trans('predictiontalk-merit-system.forum.dashboard.top_receivers_monthly')}
                        </Button>
                        <Button
                            className={'Button' + (this.receiversTab === 'weekly' ? ' active' : '')}
                            onclick={() => { this.receiversTab = 'weekly'; }}
                        >
                            {app.translator.trans('predictiontalk-merit-system.forum.dashboard.top_receivers_weekly')}
                        </Button>
                    </div>
                    {this.renderReceiversTable(data.topReceivers?.[this.receiversTab] || [])}
                </div>

                {/* Most Highlighted Posts */}
                <div className="dashboard-section">
                    <h3>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.highlighted_posts')}</h3>
                    {data.highlightedPosts?.length > 0
                        ? <table>
                            <thead>
                                <tr>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.post')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.user')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.highlights')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.highlightedPosts.map((p: any) => (
                                    <tr>
                                        <td>
                                            {p.discussionId
                                                ? <Link href={app.route('discussion', { id: p.discussionId })}>Post #{p.postId}</Link>
                                                : 'Post #' + p.postId
                                            }
                                        </td>
                                        <td>{p.author?.displayName || '-'}</td>
                                        <td>{p.highlightCount}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        : <div className="no-data">{app.translator.trans('predictiontalk-merit-system.forum.dashboard.no_highlights')}</div>
                    }
                </div>

                {/* Active Sources */}
                <div className="dashboard-section">
                    <h3>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.active_sources')}</h3>
                    {data.activeSources?.length > 0
                        ? <table>
                            <thead>
                                <tr>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.source')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.budget')}</th>
                                    <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.remaining')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.activeSources.map((s: any) => (
                                    <tr>
                                        <td>
                                            {s.user
                                                ? <Link href={app.route('user', { username: s.user.username })}>{s.user.displayName}</Link>
                                                : '-'
                                            }
                                        </td>
                                        <td>{s.monthlyBudget}</td>
                                        <td>{s.remainingBudget}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        : <div className="no-data">No active sources.</div>
                    }
                </div>
            </div>
        );
    }

    renderReceiversTable(receivers: any[]) {
        if (receivers.length === 0) {
            return <div className="no-data">No data for this period.</div>;
        }

        return (
            <table>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.user')}</th>
                        <th>{app.translator.trans('predictiontalk-merit-system.forum.dashboard.merit_received')}</th>
                    </tr>
                </thead>
                <tbody>
                    {receivers.map((r: any, i: number) => (
                        <tr>
                            <td>{i + 1}</td>
                            <td>
                                <Link href={app.route('user', { username: r.username })}>{r.displayName}</Link>
                            </td>
                            <td>{r.totalMeritReceived}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }
}
